/**
 * IndexedDB 工具类，用于管理聊天会话存储
 */
class ConversationStorage {
  constructor() {
    this.dbName = 'AI_Merchant_DB';
    this.dbVersion = 1;
    this.storeName = 'conversations';
    this.db = null;
  }

  /**
   * 初始化数据库
   * @returns {Promise<IDBDatabase>} 数据库实例
   */
  async initDB() {
    return new Promise((resolve, reject) => {
      if (this.db) {
        resolve(this.db);
        return;
      }

      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = (event) => {
        console.error('IndexedDB open error:', event.target.error);
        reject(event.target.error);
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // 创建会话存储对象
        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, { keyPath: 'id' });
          // 创建索引以支持按时间排序
          objectStore.createIndex('updatedAt', 'updatedAt', { unique: false });
        }
      };
    });
  }

  /**
   * 获取所有会话
   * @returns {Promise<Array>} 会话列表
   */
  async getAllConversations() {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readonly');
        const objectStore = transaction.objectStore(this.storeName);
        const request = objectStore.getAll();

        request.onerror = (event) => {
          console.error('Failed to get conversations:', event.target.error);
          reject(event.target.error);
        };

        request.onsuccess = (event) => {
          const conversations = event.target.result || [];
          // 按更新时间降序排序
          conversations.sort((a, b) => b.updatedAt - a.updatedAt);
          resolve(conversations);
        };
      });
    } catch (error) {
      console.error('Error getting conversations:', error);
      return [];
    }
  }

  /**
   * 保存单个会话
   * @param {Object} conversation 会话对象
   * @returns {Promise<boolean>} 是否保存成功
   */
  async saveConversation(conversation) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        const request = objectStore.put(conversation);

        request.onerror = (event) => {
          console.error('Failed to save conversation:', event.target.error);
          reject(event.target.error);
        };

        request.onsuccess = () => {
          resolve(true);
        };
      });
    } catch (error) {
      console.error('Error saving conversation:', error);
      return false;
    }
  }

  /**
   * 保存多个会话
   * @param {Array} conversations 会话数组
   * @returns {Promise<boolean>} 是否保存成功
   */
  async saveConversations(conversations) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        
        let completed = 0;
        let hasError = false;
        
        // 清空现有数据
        const clearRequest = objectStore.clear();
        
        clearRequest.onerror = (event) => {
          console.error('Failed to clear conversations:', event.target.error);
          reject(event.target.error);
        };
        
        clearRequest.onsuccess = () => {
          // 添加新数据
          conversations.forEach(conversation => {
            const request = objectStore.add(conversation);
            
            request.onerror = (event) => {
              console.error('Failed to add conversation:', event.target.error);
              hasError = true;
            };
            
            request.onsuccess = () => {
              completed++;
              if (completed === conversations.length) {
                resolve(!hasError);
              }
            };
          });
        };
      });
    } catch (error) {
      console.error('Error saving conversations:', error);
      return false;
    }
  }

  /**
   * 获取单个会话
   * @param {string} conversationId 会话ID
   * @returns {Promise<Object|null>} 会话对象或null
   */
  async getConversation(conversationId) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readonly');
        const objectStore = transaction.objectStore(this.storeName);
        const request = objectStore.get(conversationId);

        request.onerror = (event) => {
          console.error('Failed to get conversation:', event.target.error);
          reject(event.target.error);
        };

        request.onsuccess = (event) => {
          resolve(event.target.result || null);
        };
      });
    } catch (error) {
      console.error('Error getting conversation:', error);
      return null;
    }
  }

  /**
   * 删除会话
   * @param {string} conversationId 会话ID
   * @returns {Promise<boolean>} 是否删除成功
   */
  async deleteConversation(conversationId) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        const request = objectStore.delete(conversationId);

        request.onerror = (event) => {
          console.error('Failed to delete conversation:', event.target.error);
          reject(event.target.error);
        };

        request.onsuccess = () => {
          resolve(true);
        };
      });
    } catch (error) {
      console.error('Error deleting conversation:', error);
      return false;
    }
  }

  /**
   * 清空所有会话
   * @returns {Promise<boolean>} 是否清空成功
   */
  async clearAllConversations() {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        const request = objectStore.clear();

        request.onerror = (event) => {
          console.error('Failed to clear conversations:', event.target.error);
          reject(event.target.error);
        };

        request.onsuccess = () => {
          resolve(true);
        };
      });
    } catch (error) {
      console.error('Error clearing conversations:', error);
      return false;
    }
  }

  /**
   * 从localStorage迁移数据到IndexedDB
   * @returns {Promise<boolean>} 是否迁移成功
   */
  async migrateFromLocalStorage() {
    try {
      // 检查localStorage中是否有数据
      const localStorageData = localStorage.getItem('chat_conversations');
      if (!localStorageData) {
        return true; // 没有数据需要迁移
      }

      const conversations = JSON.parse(localStorageData);
      if (conversations.length === 0) {
        return true;
      }

      // 保存到IndexedDB
      const success = await this.saveConversations(conversations);
      if (success) {
        // 迁移成功后，清除localStorage数据
        localStorage.removeItem('chat_conversations');
        console.log('Successfully migrated conversations from localStorage to IndexedDB');
      }
      
      return success;
    } catch (error) {
      console.error('Error migrating from localStorage:', error);
      return false;
    }
  }
}

// 创建单例实例
const conversationStorage = new ConversationStorage();

export default conversationStorage;